import React, { useState } from 'react';
import { X, Download, AlertCircle, CheckCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { executeManualExport, downloadExportFile } from '../../../services/erpIntegrationService';

const ManualExportWizard = ({ integrations, companyId, onClose }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    integrationId: '',
    dataType: 'employees',
    format: 'CSV',
    startDate: '',
    endDate: '',
    filters: {}
  });
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [error, setError] = useState('');

  const handleNext = () => {
    if (step === 1 && !formData?.integrationId) {
      setError('Please select an ERP integration');
      return;
    }
    setError('');
    setStep(step + 1);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setError('');

      const result = await executeManualExport({
        ...formData,
        filters: {
          companyId,
          startDate: formData?.startDate,
          endDate: formData?.endDate
        }
      });

      setExportResult(result);
      setStep(4);
    } catch (err) {
      setError(err?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleDownload = () => {
    if (exportResult?.data) {
      const timestamp = new Date()?.toISOString()?.split('T')?.[0];
      const filename = `${formData?.dataType}_export_${timestamp}`;
      downloadExportFile(exportResult?.data, formData?.format, filename);
    }
  };

  const selectedIntegration = integrations?.find(i => i?.id === formData?.integrationId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Manual Data Export
          </h2>
          <button
            onClick={() => onClose(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-8">
            {[1, 2, 3, 4]?.map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    s <= step
                      ? 'bg-blue-600 text-white' :'bg-gray-200 text-gray-600'
                  }`}
                >
                  {s}
                </div>
                {s < 4 && (
                  <div
                    className={`w-20 h-1 mx-2 ${
                      s < step ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {error && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3 mb-6">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">{error}</p>
            </div>
          )}

          {/* Step 1: Select Integration */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Step 1: Select ERP Integration
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ERP System *
                </label>
                <Select
                  value={formData?.integrationId}
                  onChange={(e) => setFormData({ ...formData, integrationId: e?.target?.value })}
                  required
                >
                  <option value="">Select an integration</option>
                  {integrations?.filter(i => i?.status === 'active')?.map((integration) => (
                    <option key={integration?.id} value={integration?.id}>
                      {integration?.erp_system_name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          )}

          {/* Step 2: Select Data Type */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Step 2: Select Data Type
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data to Export *
                </label>
                <Select
                  value={formData?.dataType}
                  onChange={(e) => setFormData({ ...formData, dataType: e?.target?.value })}
                >
                  <option value="employees">Employee Records</option>
                  <option value="payroll">Payroll Data</option>
                  <option value="attendance">Attendance Records</option>
                  <option value="leave">Leave Records</option>
                  <option value="compliance">Compliance Reports</option>
                </Select>
              </div>

              {formData?.dataType === 'payroll' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date
                    </label>
                    <Input
                      type="date"
                      value={formData?.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e?.target?.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <Input
                      type="date"
                      value={formData?.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e?.target?.value })}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Select Format */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Step 3: Select Export Format
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Export Format *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {selectedIntegration?.erp_integrations?.exportFormats?.map((format) => (
                    <button
                      key={format}
                      onClick={() => setFormData({ ...formData, format })}
                      className={`p-4 border-2 rounded-lg text-left transition-colors ${
                        formData?.format === format
                          ? 'border-blue-600 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className="font-semibold text-gray-900">{format}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {format === 'CSV' && 'Comma-separated values'}
                        {format === 'Excel' && 'Microsoft Excel format'}
                        {format === 'JSON' && 'JavaScript Object Notation'}
                        {format === 'XML' && 'Extensible Markup Language'}
                        {format === 'API' && 'Direct API integration'}
                      </p>
                    </button>
                  )) || (
                    ['CSV', 'JSON', 'XML']?.map((format) => (
                      <button
                        key={format}
                        onClick={() => setFormData({ ...formData, format })}
                        className={`p-4 border-2 rounded-lg text-left transition-colors ${
                          formData?.format === format
                            ? 'border-blue-600 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <p className="font-semibold text-gray-900">{format}</p>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Export Complete */}
          {step === 4 && exportResult && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Export Completed Successfully!
              </h3>
              <p className="text-gray-600 mb-6">
                {exportResult?.recordCount} records exported in {formData?.format} format
              </p>
              <Button
                onClick={handleDownload}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Download File
              </Button>
            </div>
          )}

          {/* Navigation Buttons */}
          {step < 4 && (
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
              <Button
                onClick={() => setStep(step - 1)}
                variant="outline"
                disabled={step === 1}
              >
                Previous
              </Button>
              {step < 3 ? (
                <Button
                  onClick={handleNext}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Next
                </Button>
              ) : (
                <Button
                  onClick={handleExport}
                  disabled={exporting}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {exporting ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin">⟳</span>
                      Exporting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Download className="w-4 h-4" />
                      Start Export
                    </span>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManualExportWizard;